// Small colour helpers for brand theming (no dependencies).
// Hex is always in "#rrggbb" form.

export function hexToRgb(hex: string): [number, number, number] | null {
    const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex(r: number, g: number, b: number): string {
    const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
    return `#${c(r)}${c(g)}${c(b)}`;
}

export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0;
    const l = (max + min) / 2;
    const d = max - min;
    const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    if (d !== 0) {
        switch (max) {
            case r: h = ((g - b) / d) % 6; break;
            case g: h = (b - r) / d + 2; break;
            default: h = (r - g) / d + 4; break;
        }
        h *= 60;
        if (h < 0) h += 360;
    }
    return [h, s, l];
}

export function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;
    if (h < 60) { r = c; g = x; }
    else if (h < 120) { r = x; g = c; }
    else if (h < 180) { g = c; b = x; }
    else if (h < 240) { g = x; b = c; }
    else if (h < 300) { r = x; b = c; }
    else { r = c; b = x; }
    return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/** Darken a hex colour by reducing lightness toward 0 (factor 0..1 of current L). */
export function darken(hex: string, factor: number): string {
    const rgb = hexToRgb(hex); if (!rgb) return hex;
    const [h, s, l] = rgbToHsl(...rgb);
    const [r, g, b] = hslToRgb(h, s, clamp01(l * (1 - factor)));
    return rgbToHex(r, g, b);
}

/** A pale tint of the colour for table-header / wash backgrounds. */
export function tint(hex: string): string {
    const rgb = hexToRgb(hex); if (!rgb) return hex;
    const [h, s] = rgbToHsl(...rgb);
    const [r, g, b] = hslToRgb(h, Math.min(s, 0.45), 0.90);
    return rgbToHex(r, g, b);
}

export interface BrandShades {
    primary: string;
    dark: string;
    darker: string;
    light: string;
}

/**
 * Derive the report palette from a single base brand colour, mirroring the
 * relationships of the default green ($brand-primary/dark/darker/light).
 */
export function deriveBrandShades(hex: string): BrandShades {
    return {
        primary: hex,
        dark: darken(hex, 0.28),
        darker: darken(hex, 0.45),
        light: tint(hex),
    };
}

/** True for near-white/near-black/low-saturation pixels we don't want as a brand colour. */
function isNeutral(r: number, g: number, b: number): boolean {
    const [, s, l] = rgbToHsl(r, g, b);
    return s < 0.18 || l > 0.92 || l < 0.08;
}

/**
 * Pick a representative brand colour from image pixel data: the most common
 * "vibrant" (non-neutral, opaque) colour, bucketed by hue. Returns hex or null
 * when the image is essentially greyscale (e.g. a black-on-white logo).
 */
export function dominantBrandColour(data: Uint8ClampedArray): string | null {
    // Hue buckets (12 × 30°); accumulate saturation-weighted RGB sums.
    const buckets = Array.from({ length: 12 }, () => ({ w: 0, r: 0, g: 0, b: 0 }));
    for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a < 128) continue;
        const r = data[i], g = data[i + 1], b = data[i + 2];
        if (isNeutral(r, g, b)) continue;
        const [h, s] = rgbToHsl(r, g, b);
        const bucket = buckets[Math.min(11, Math.floor(h / 30))];
        const w = s; // weight vibrant pixels more
        bucket.w += w; bucket.r += r * w; bucket.g += g * w; bucket.b += b * w;
    }
    let best = buckets[0];
    for (const bk of buckets) if (bk.w > best.w) best = bk;
    if (best.w === 0) return null;
    return rgbToHex(best.r / best.w, best.g / best.w, best.b / best.w);
}
